import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Spinner, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getRecipes } from '../../../services/RecipesService';
import { getMaterials } from '../../../services/MaterialLogsService';
import { toast } from 'react-toastify';
import { quantityToGrams, formatUnitForDisplay } from '../../../utils/unitConversion';
import { roundNutrient, fmtNut } from '../../../utils/nutritionUtils';
import { getMaterialEnglishName, getMaterialName } from '../../../utils/materialName';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function nutritionPerPurchaseQuantity(mat) {
    const grams = quantityToGrams(mat.quantity_purchased, mat.unit);
    const factor = grams / 100;
    const scale = (v) => v != null ? roundNutrient(v * factor, 2) : null;
    return {
        energy:       scale(mat.energy_per_100g),
        protein:      scale(mat.protein_per_100g),
        fat:          scale(mat.fat_per_100g),
        carbohydrate: scale(mat.carbohydrate_per_100g),
        fiber:        scale(mat.fiber_per_100g),
        sodium:       scale(mat.sodium_per_100g),
        purchaseLabel: `${mat.quantity_purchased ?? 1} ${formatUnitForDisplay(mat.unit)}`,
    };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function RecipesNutritionFacts() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [recipes, setRecipes] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const [rRes, mRes] = await Promise.all([
                    getRecipes(),
                    getMaterials(),
                ]);
                setRecipes(rRes.data || []);
                setMaterials(mRes.data || []);
            } catch {
                toast.error(t('nutritionFacts.errors.load'));
                setRecipes([]);
                setMaterials([]);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const recipesWithNutrition = useMemo(() => {
        const matById = {};
        for (const m of materials) matById[m.id] = m;

        return recipes.map((r) => {
            let energy = 0, protein = 0, fat = 0, carb = 0, fiber = 0, sodium = 0;
            let hasSodium = false;
            let hasIncomplete = false;

            for (const it of r.items || []) {
                const mat = matById[it.material_id];
                if (!mat || mat.is_non_food) continue;
                const e = mat.energy_per_100g, p = mat.protein_per_100g;
                const f = mat.fat_per_100g, c = mat.carbohydrate_per_100g;
                if (e == null || p == null || f == null || c == null) { hasIncomplete = true; continue; }
                const grams = quantityToGrams(it.quantity, it.material_unit || it.unit || mat.unit);
                const factor = grams / 100;
                energy += factor * e;
                protein += factor * p;
                fat += factor * f;
                carb += factor * c;
                fiber += factor * (mat.fiber_per_100g || 0);
                if (mat.sodium_per_100g != null) { sodium += factor * mat.sodium_per_100g; hasSodium = true; }
            }

            const yieldAmt = parseFloat(r.yield_amount) || 1;
            const r1 = (v) => roundNutrient(v, 1);
            const r0 = (v) => roundNutrient(v, 0);

            const perServing = yieldAmt > 1
                ? {
                    energy:       r0(energy / yieldAmt),
                    protein:      r1(protein / yieldAmt),
                    fat:          r1(fat / yieldAmt),
                    carbohydrate: r1(carb / yieldAmt),
                    fiber:        r1(fiber / yieldAmt),
                    sodium:       hasSodium ? roundNutrient(sodium / yieldAmt, 4) : null,
                }
                : null;

            const yieldUnit = r.yield_unit || 'unit';
            const servingDisplayLabel = yieldAmt > 1 ? `1 ${yieldUnit}` : `${r.yield_amount ?? 1} ${yieldUnit}`;

            return {
                ...r,
                nutrition: {
                    energy:       r0(energy),
                    protein:      r1(protein),
                    fat:          r1(fat),
                    carbohydrate: r1(carb),
                    fiber:        r1(fiber),
                    sodium:       hasSodium ? roundNutrient(sodium, 4) : null,
                    perServing,
                },
                hasIncomplete,
                yieldLabel: `${r.yield_amount ?? 1} ${yieldUnit}`,
                servingDisplayLabel,
            };
        });
    }, [recipes, materials]);

    const materialsWithNutrition = useMemo(() => {
        return materials
            .filter((m) => !m.is_non_food)
            .map((m) => ({ ...m, nutritionPerPurchase: nutritionPerPurchaseQuantity(m) }))
            .filter((m) =>
                m.nutritionPerPurchase.energy != null &&
                m.nutritionPerPurchase.protein != null &&
                m.nutritionPerPurchase.fat != null &&
                m.nutritionPerPurchase.carbohydrate != null
            );
    }, [materials]);

    const filteredRecipes = useMemo(() => {
        const q = (searchQuery || '').trim().toLowerCase();
        if (!q) return recipesWithNutrition;
        return recipesWithNutrition.filter((r) => (r.name || '').toLowerCase().includes(q));
    }, [recipesWithNutrition, searchQuery]);

    const filteredMaterials = useMemo(() => {
        const q = (searchQuery || '').trim().toLowerCase();
        if (!q) return materialsWithNutrition;
        return materialsWithNutrition.filter((m) => {
            const haystack = `${getMaterialEnglishName(m)} ${m.name_nl || ''}`.toLowerCase();
            return haystack.includes(q);
        });
    }, [materialsWithNutrition, searchQuery, i18n.resolvedLanguage]);

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" className="text-primary" />
                    <p className="mt-2 text-muted mb-0">{t('nutritionFacts.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid pb-5 nutrition-facts-content" style={{ minHeight: 0 }}>
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <div>
                                <h4 className="heading mb-0">{t('nutritionFacts.title')}</h4>
                                <span className="text-muted">
                                    {t('nutritionFacts.recipesCount', { count: recipes.length })}
                                </span>
                            </div>
                            <div className="d-flex align-items-center gap-2 mobile-toolbar nutrition-facts-toolbar">
                                <div className="input-group mobile-toolbar-search" style={{ minWidth: 280, width: 360 }}>
                                    <span className="input-group-text bg-light">
                                        <i className="fa-solid fa-magnifying-glass text-muted" />
                                    </span>
                                    <Form.Control
                                        type="search"
                                        placeholder={t('nutritionFacts.searchPlaceholder')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        aria-label={t('common.search')}
                                    />
                                    {searchQuery && (
                                        <button type="button" className="btn btn-light" onClick={() => setSearchQuery('')} title={t('common.clearSearch')} aria-label={t('common.clearSearch')}>
                                            <i className="fa-solid fa-xmark text-muted" />
                                        </button>
                                    )}
                                </div>
                                <Button variant="outline-primary" size="sm" onClick={() => navigate('/recipes')} className="mobile-toolbar-add-btn">
                                    <i className="fa-solid fa-list me-1" /> {t('nutritionFacts.backToRecipes')}
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {searchQuery && (recipes.length > 0 || materialsWithNutrition.length > 0) && (
                                <div className="px-3 pt-2">
                                    <small className="text-muted">
                                        {t('nutritionFacts.showing', { recipes: filteredRecipes.length, materials: filteredMaterials.length })}
                                        <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={() => setSearchQuery('')}>{t('common.clearSearch')}</button>
                                    </small>
                                </div>
                            )}
                            {recipes.length === 0 ? (
                                <div className="text-center py-5">
                                    <p className="text-muted mb-2">{t('nutritionFacts.noRecipesYet')}</p>
                                    <Button variant="primary" onClick={() => navigate('/recipes')}>{t('nutritionFacts.goToRecipes')}</Button>
                                </div>
                            ) : (
                                <Table responsive hover className="mb-0 nutrition-facts-table">
                                    <thead>
                                        <tr>
                                            <th>{t('nutritionFacts.recipe')}</th>
                                            <th>{t('nutritionFacts.serving')}</th>
                                            <th>{t('nutritionFacts.energy')}</th>
                                            <th>{t('nutritionFacts.fat')}</th>
                                            <th>{t('nutritionFacts.carbs')}</th>
                                            <th>{t('nutritionFacts.fiber')}</th>
                                            <th>{t('nutritionFacts.protein')}</th>
                                            <th>{t('nutritionFacts.sodium')}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRecipes.map((r) => {
                                            const yieldAmt = parseFloat(r.yield_amount) || 1;
                                            const n = yieldAmt > 1 ? r.nutrition.perServing : r.nutrition;
                                            return (
                                                <tr key={r.id}>
                                                    <td>
                                                        <strong>{r.name}</strong>
                                                        {r.hasIncomplete && (
                                                            <small className="d-block text-warning">{t('nutritionFacts.someIngredientsMissing')}</small>
                                                        )}
                                                    </td>
                                                    <td>{r.servingDisplayLabel}</td>
                                                    <td>{fmtNut(n?.energy, 0)}</td>
                                                    <td>{fmtNut(n?.fat)}</td>
                                                    <td>{fmtNut(n?.carbohydrate)}</td>
                                                    <td>{fmtNut(n?.fiber)}</td>
                                                    <td>{fmtNut(n?.protein)}</td>
                                                    <td>{n?.sodium != null ? fmtNut(n.sodium * 1000, 0) : '—'}</td>
                                                    <td>
                                                        <Button variant="light" size="sm" onClick={() => navigate(`/recipes/${r.id}`)}>{t('nutritionFacts.view')}</Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>

                    {materialsWithNutrition.length > 0 && (
                        <Card className="mt-4">
                            <Card.Header>
                                <span className="text-muted">{t('nutritionFacts.materialsSection')}</span>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <Table responsive hover className="mb-0 nutrition-facts-table">
                                    <thead>
                                        <tr>
                                            <th>{t('nutritionFacts.material')}</th>
                                            <th>{t('nutritionFacts.purchaseQty')}</th>
                                            <th>{t('nutritionFacts.energy')}</th>
                                            <th>{t('nutritionFacts.fat')}</th>
                                            <th>{t('nutritionFacts.carbs')}</th>
                                            <th>{t('nutritionFacts.fiber')}</th>
                                            <th>{t('nutritionFacts.protein')}</th>
                                            <th>{t('nutritionFacts.sodium')}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredMaterials.map((m) => {
                                            const n = m.nutritionPerPurchase;
                                            return (
                                                <tr key={m.id}>
                                                    <td><strong>{getMaterialName(m)}</strong></td>
                                                    <td>{n.purchaseLabel}</td>
                                                    <td>{fmtNut(n.energy, 0)}</td>
                                                    <td>{fmtNut(n.fat)}</td>
                                                    <td>{fmtNut(n.carbohydrate)}</td>
                                                    <td>{n.fiber != null ? fmtNut(n.fiber) : '0.0'}</td>
                                                    <td>{fmtNut(n.protein)}</td>
                                                    <td>{n.sodium != null ? fmtNut(n.sodium * 1000, 0) : '—'}</td>
                                                    <td>
                                                        <Button variant="light" size="sm" onClick={() => navigate('/materials')}>{t('common.edit')}</Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}
